import {
  CustomerSupportType,
  Feature as FeatureLib,
  isFeatureAvailable,
  PaymentCardType,
} from '@shinetools/pricing-plan-library';

import { PlansVariant } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import amountWithMinorCurrency from 'features/Onboarding/modules/PricingPlan/helpers/amountWithMinorCurrency';
import { ActiveProviderPlanId } from 'types/planUtils';

import { OnboardingPlanDisplay } from '../../../../types';
import { Feature, FeatureKey } from '../../types';
import locales from './locales';

type FeaturesBuilderProps = (
  plan: Pick<
    OnboardingPlanDisplay,
    'providerPlanId' | 'features' | 'paymentCard' | 'limits' | 'customerSupport'
  >,
  variant?: PlansVariant | null,
) => Feature[];

// features based on the same model for all plans
const cardsExceptBasic = (
  plan: Pick<
    OnboardingPlanDisplay,
    'providerPlanId' | 'features' | 'paymentCard' | 'limits' | 'customerSupport'
  >,
): Feature => {
  return {
    key: FeatureKey.Cards,
    rows: [
      {
        isActive: true,
        label: `${plan.limits.physicalPaymentCard.value} ${
          plan.paymentCard.type === PaymentCardType.BasicDebitCard
            ? 'Mastercard Basic Debit'
            : 'Mastercard Business World Debit'
        } `,
      },
      {
        isActive: true,
        label: plan.limits.virtualPaymentCardMonth.isUnlimited
          ? locales.illimitedVirtualCards
          : locales.formatString(locales.virtualCards, {
              count: plan.limits.virtualPaymentCardMonth.value,
            }),
      },
    ],
  };
};

const transfersExceptBasic = (
  plan: Pick<
    OnboardingPlanDisplay,
    'providerPlanId' | 'features' | 'paymentCard' | 'limits' | 'customerSupport'
  >,
): Feature => {
  return {
    key: FeatureKey.Transfers,
    rows: [
      {
        isActive: true,
        label: locales.formatString(locales.transfers, {
          count: plan.limits.sepaTransferMonth.value,
        }),
      },
      {
        isActive: true,
        label: locales.formatString(locales.whithdrawalsWithoutFees, {
          count: plan.limits.withdrawalMonth.value,
        }),
      },
      {
        isActive: true,
        label: locales.formatString(locales.checkDepositMonth, {
          count: plan.limits.checkDepositMonth.value,
        }),
      },
      {
        isActive: true,
        label:
          plan.limits.cashDepositMonth.value > 1
            ? locales.formatString(locales.cashDeposits, {
                count: plan.limits.cashDepositMonth.value,
                value: formatAmount({
                  forcePlusSign: false,
                  value: amountWithMinorCurrency(
                    plan.limits.cashDepositMaxAmountPerDeposit.value,
                  ),
                  withNoMinorUnitDigits: true,
                }),
              })
            : locales.formatString(locales.cashDepositSingle, {
                count: plan.limits.cashDepositMonth.value,
                value: formatAmount({
                  forcePlusSign: false,
                  value: amountWithMinorCurrency(
                    plan.limits.cashDepositMaxAmountPerDeposit.value,
                  ),
                  withNoMinorUnitDigits: true,
                }),
              }),
      },
    ],
  };
};

const bulkTransfer = (
  plan: Pick<
    OnboardingPlanDisplay,
    'providerPlanId' | 'features' | 'paymentCard' | 'limits' | 'customerSupport'
  >,
): Feature => {
  return {
    key: FeatureKey.SupplementaryFunctionalities,
    rows: [
      {
        isActive: isFeatureAvailable(plan, FeatureLib.BulkTransfers),
        label: locales.bulkTransfers,
      },
      {
        isActive: isFeatureAvailable(plan, FeatureLib.TeamManagement),
        label: locales.accountantAccess,
      },
    ],
  };
};

const support = (
  plan: Pick<
    OnboardingPlanDisplay,
    'providerPlanId' | 'features' | 'paymentCard' | 'limits' | 'customerSupport'
  >,
): Feature => {
  return {
    key: FeatureKey.Support,
    rows: [
      {
        isActive: true,
        label:
          plan.customerSupport.type === CustomerSupportType.Standard
            ? locales.standardSupport
            : locales.prioritySupport,
      },
      {
        isActive: plan.customerSupport.type === CustomerSupportType.PriorityAll,
        label: locales.phoneCallBack,
      },
    ],
  };
};

const guarantyAndInsurance = (
  plan: Pick<
    OnboardingPlanDisplay,
    'providerPlanId' | 'features' | 'paymentCard' | 'limits' | 'customerSupport'
  >,
): Feature => {
  return {
    key: FeatureKey.GuarantyAndInsurance,
    rows: [
      {
        isActive: isFeatureAvailable(plan, FeatureLib.InsuranceAssistance),
        label: locales.legalAssistance,
      },
      {
        isActive: isFeatureAvailable(plan, FeatureLib.InsuranceAssistance),
        label: locales.accidentGuaranty,
      },
      {
        isActive: isFeatureAvailable(plan, FeatureLib.InsuranceAssistance),
        label: locales.phoneProtection,
      },
      {
        isActive: isFeatureAvailable(plan, FeatureLib.InsuranceAssistance),
        label: locales.materialProtections,
      },
    ],
  };
};

// Gather custom features and common features
const featuresBuilder: Record<ActiveProviderPlanId, FeaturesBuilderProps> = {
  basic: (plan) => {
    return [
      {
        key: FeatureKey.AccountAccess,
        rows: [
          {
            isActive: true,
            label: locales.formatString(locales.accountAcces, {
              count: plan.limits.companyUserAccess.value,
            }),
          },
        ],
      },
      {
        key: FeatureKey.Cards,
        rows: [
          {
            isActive: true,
            label: `${plan.limits.physicalPaymentCard.value} ${
              plan.paymentCard.type === PaymentCardType.BasicDebitCard
                ? 'Mastercard Basic Debit'
                : ' Business World Debit'
            } `,
          },
          {
            isActive: true,
            label: locales.optionnalVirtualCard,
          },
        ],
      },
      {
        key: FeatureKey.Transfers,
        rows: [
          {
            isActive: true,
            label: locales.formatString(locales.transfers, {
              count: plan.limits.sepaTransferMonth.value,
            }),
          },
          {
            isActive: true,
            label: `${locales.withdrawalsToPay}`,
          },
          {
            isActive: false,
            label: `${locales.checkDeposit}`,
          },
          {
            isActive: false,
            label: `${locales.cashDeposit}`,
          },
        ],
      },
      bulkTransfer(plan),
      support(plan),
      guarantyAndInsurance(plan),
    ];
  },
  business: (plan) => {
    return [
      {
        key: FeatureKey.AccountAccess,
        rows: [
          {
            isActive: true,
            label: locales.formatString(locales.accountAccesses, {
              count: plan.limits.companyUserAccess.value,
            }),
          },
          {
            isActive: isFeatureAvailable(plan, FeatureLib.TeamManagement),
            label: locales.spentManagement,
          },
        ],
      },
      cardsExceptBasic(plan),
      transfersExceptBasic(plan),
      bulkTransfer(plan),
      support(plan),
      guarantyAndInsurance(plan),
    ];
  },
  plus: (plan) => {
    return [
      {
        key: FeatureKey.AccountAccess,
        rows: [
          {
            isActive: true,
            label: locales.formatString(locales.accountAcces, {
              count: plan.limits.companyUserAccess.value,
            }),
          },
        ],
      },
      cardsExceptBasic(plan),
      transfersExceptBasic(plan),
      bulkTransfer(plan),
      support(plan),
      guarantyAndInsurance(plan),
    ];
  },
  pro: (plan, variant) => {
    return [
      {
        key: FeatureKey.AccountAccess,
        rows:
          variant === PlansVariant.Solo
            ? [
                {
                  isActive: true,
                  label: locales.formatString(locales.accountAcces, {
                    count: plan.limits.companyUserAccess.value,
                  }),
                },
              ]
            : [
                {
                  isActive: true,
                  label: locales.formatString(locales.accountAccesses, {
                    count: plan.limits.companyUserAccess.value,
                  }),
                },
                {
                  isActive: isFeatureAvailable(plan, FeatureLib.TeamManagement),
                  label: locales.spentManagement,
                },
              ],
      },
      cardsExceptBasic(plan),
      transfersExceptBasic(plan),
      bulkTransfer(plan),
      support(plan),
      guarantyAndInsurance(plan),
    ];
  },
};
export default featuresBuilder;
